import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  MapPin, 
  Users, 
  Clock, 
  Heart,
  CheckCircle2,
  Truck,
  ShoppingBag,
  Mail,
  Phone,
  Globe,
  ArrowLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import OtrokoviceForm from '../components/OtrokoviceForm';
import { 
  festivalInfo, 
  heroImage,
  aboutFestival,
  programSchedule, 
  whyJoinReasons, 
  vendorCategories,
  whatWeOffer,
  scheduleInfo,
  venueInfo,
  importantRules, 
  pricingInfo,
  contactInfo
} from '../data/otrokoviceData';

const OtrokoviceLanding = () => {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);

  const scrollToForm = () => {
    setShowForm(true);
    setTimeout(() => {
      document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const iconComponents = {
    Users, Clock, Calendar, CheckCircle2, Heart, Globe, MapPin
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-blue-50">
      {/* Header - Tropical/Summer Theme */}
      <header className="fixed top-0 w-full bg-gradient-to-r from-cyan-500 via-blue-400 to-cyan-500 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-white hover:text-pink-200"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Přehled akcí</span>
            </Button>
            <div className="text-2xl font-bold leading-tight">
              <div className="flex flex-col">
                <span className="text-white text-sm tracking-wide">OTROKOVICKÉ</span>
                <span className="text-pink-200 italic text-lg" style={{fontFamily: 'cursive'}}>letní</span>
                <span className="text-white text-sm tracking-wide">SLAVNOSTI</span>
              </div>
            </div>
          </div>
          <Button 
            onClick={scrollToForm}
            className="bg-pink-500 hover:bg-pink-600 text-white font-semibold px-8 py-2 rounded-full shadow-lg hover:shadow-xl transition-all"
          >
            PŘIHLÁSIT SE
          </Button>
        </div>
      </header>

      {/* Hero Section - Tropical Summer Design */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 text-white overflow-hidden">
        {/* Background Image with Cyan Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src={heroImage}
            alt="Otrokovické letní slavnosti"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-600/80 via-blue-500/80 to-cyan-600/80"></div>
          {/* Tropical accent bars */}
          <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-pink-400 to-transparent opacity-30"></div>
          <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-pink-400 to-transparent opacity-30"></div>
        </div>
        
        <div className="max-w-7xl mx-auto text-center relative z-10">
          {/* Subtitle */}
          <div className="mb-4">
            <p className="text-pink-200 text-lg tracking-widest font-semibold">PRODEJ NA</p>
          </div>
          
          {/* Main Title - Tropical Style */}
          <div className="mb-8">
            <h1 className="text-6xl md:text-7xl font-bold mb-4 leading-tight">
              <span className="text-white">OTROKOVICKÉ</span><br/>
              <span className="text-pink-300">letní SLAVNOSTI</span>
            </h1>
          </div>

          {/* Badge - Free Entry */}
          <div className="inline-block bg-gradient-to-r from-pink-500 to-orange-500 text-white px-10 py-4 rounded-full font-bold text-xl mb-8 shadow-2xl">
            {festivalInfo.entrance}
          </div>
          
          {/* Date and Location */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 text-xl mb-10">
            <div className="flex items-center gap-3">
              <Calendar className="h-6 w-6 text-pink-300" />
              <span className="font-semibold">{festivalInfo.date}</span>
            </div>
            <div className="flex items-center gap-3">
              <MapPin className="h-6 w-6 text-pink-300" />
              <span className="font-semibold">{festivalInfo.location}</span>
            </div>
          </div>

          {/* Subtitle */}
          <p className="text-2xl md:text-3xl mb-10 max-w-4xl mx-auto font-light leading-relaxed">
            {festivalInfo.subtitle}
          </p>

          {/* CTA Button */}
          <Button 
            size="lg" 
            onClick={scrollToForm}
            className="bg-pink-500 hover:bg-pink-600 text-white text-xl px-12 py-6 rounded-full font-bold shadow-2xl hover:shadow-pink-500/50 transition-all transform hover:scale-105"
          >
            PŘIHLÁSIT SE JAKO PRODEJCE
          </Button>
        </div>
      </section>

      {/* PROČ PRODÁVAT Section - 5 kart */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-cyan-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl font-bold text-center mb-4">
            <span className="text-cyan-600">PROČ PRODÁVAT</span> <span className="text-gray-800">NA AKCI</span>
          </h2>
          
          {/* Grid pro 5 karet - 3 nahoře, 2 dole vycentrované */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-16 max-w-6xl mx-auto">
            {whyJoinReasons.map((reason, index) => {
              const IconComponent = iconComponents[reason.icon];
              return (
                <Card key={index} className={`text-center hover:shadow-xl transition-all bg-white border-t-4 border-cyan-500 ${index >= 3 ? 'md:col-span-1 lg:col-start-auto' : ''}`}>
                  <CardHeader>
                    <div className="flex justify-center mb-4">
                      <div className="bg-gradient-to-br from-cyan-500 to-blue-500 p-4 rounded-full">
                        <IconComponent className="h-8 w-8 text-white" />
                      </div>
                    </div>
                    <CardTitle className="text-2xl text-gray-800">{reason.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base text-gray-600">
                      {reason.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CO HLEDÁME Section - KONTAKT Fest Style */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">KOHO HLEDÁME?</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Vybíráme prodejce s důrazem na kvalitu a zkušenosti
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Priority - Food Trucks */}
            <div className="border-2 border-cyan-500 rounded-lg overflow-hidden">
              <div className="bg-cyan-500 p-8">
                <div className="flex items-center gap-4 mb-4">
                  <Truck className="w-12 h-12 text-white" />
                  <div>
                    <div className="text-sm font-bold uppercase tracking-wider mb-1">Priorita</div>
                    <h3 className="text-3xl font-bold">{vendorCategories.priority.title}</h3>
                  </div>
                </div>
                <p className="text-cyan-50 text-lg">{vendorCategories.priority.description}</p>
              </div>
              <div className="bg-white p-8 text-gray-900">
                <ul className="space-y-4 mb-6">
                  {vendorCategories.priority.requirements.map((req, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-cyan-500 flex-shrink-0 mt-0.5" />
                      <span className="text-lg">{req}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-cyan-50 border-l-4 border-cyan-500 p-4">
                  <p className="text-sm text-gray-700">
                    <strong className="text-gray-900">Důvod:</strong> {vendorCategories.priority.reason}
                  </p>
                </div>
              </div>
            </div>

            {/* Additional Sales */}
            <div className="border-2 border-gray-600 rounded-lg overflow-hidden">
              <div className="bg-gray-700 p-8">
                <div className="flex items-center gap-4 mb-4">
                  <ShoppingBag className="w-12 h-12 text-white" />
                  <div>
                    <h3 className="text-3xl font-bold">{vendorCategories.additional.title}</h3>
                  </div>
                </div>
              </div>
              <div className="bg-white p-8 text-gray-900">
                <ul className="space-y-4">
                  {vendorCategories.additional.categories.map((cat, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-pink-500 flex-shrink-0 mt-0.5" />
                      <span className="text-lg">{cat}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TERMÍN A PROVOZ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-5xl font-bold text-center mb-16">
            <span className="text-cyan-600">TERMÍN</span> <span className="text-gray-800">A PROVOZ</span>
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-t-4 border-cyan-500">
              <CardHeader>
                <CardTitle className="text-2xl text-center">
                  <Calendar className="h-8 w-8 mx-auto mb-2 text-cyan-600" />
                  Datum akce
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-xl font-semibold text-gray-800">{scheduleInfo.date}</p>
              </CardContent>
            </Card>

            <Card className="border-t-4 border-pink-500">
              <CardHeader>
                <CardTitle className="text-2xl text-center">
                  <Clock className="h-8 w-8 mx-auto mb-2 text-pink-600" />
                  Prodejní doba
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center space-y-2">
                <p className="text-gray-800"><strong>Pátek:</strong> {scheduleInfo.hours.friday}</p>
                <p className="text-gray-800"><strong>Sobota:</strong> {scheduleInfo.hours.saturday}</p>
              </CardContent>
            </Card>

            <Card className="border-t-4 border-blue-500">
              <CardHeader>
                <CardTitle className="text-2xl text-center">
                  <Truck className="h-8 w-8 mx-auto mb-2 text-blue-600" />
                  Příjezd
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-gray-800">{scheduleInfo.setup}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* MÍSTO KONÁNÍ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-cyan-50 to-blue-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-5xl font-bold text-center mb-16">
            <span className="text-cyan-600">MÍSTO</span> <span className="text-gray-800">KONÁNÍ</span>
          </h2>
          
          <Card className="border-t-4 border-cyan-500">
            <CardHeader className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white">
              <CardTitle className="text-3xl text-center flex items-center justify-center gap-3">
                <MapPin className="h-8 w-8" />
                {venueInfo.city}
              </CardTitle>
              <CardDescription className="text-center text-white text-xl mt-2">
                {venueInfo.mainLocation}
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-4 text-center">Areál festivalu:</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {venueInfo.zones.map((zone, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 bg-cyan-50 rounded-lg">
                    <CheckCircle2 className="h-6 w-6 text-cyan-600" />
                    <span className="text-lg text-gray-700">{zone}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* PROGRAM Section with Videos */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl font-bold text-center mb-4">
            <span className="text-cyan-400">PROGRAM</span>
          </h2>
          <p className="text-center text-xl text-gray-300 mb-16">Lákadlo pro tisíce návštěvníků</p>
          
          <div className="grid md:grid-cols-2 gap-12">
            {/* Friday */}
            <div>
              <h3 className="text-3xl font-bold mb-8 text-cyan-400 text-center">PÁTEK 10. 7. 2026</h3>
              <div className="space-y-6">
                {programSchedule.friday.map((item, index) => (
                  <Card key={index} className="bg-gray-800 border-cyan-500 hover:shadow-lg hover:shadow-cyan-500/50 transition-all">
                    <CardHeader>
                      <div>
                        <CardTitle className="text-2xl text-white">{item.artist}</CardTitle>
                        <CardDescription className="text-gray-400 text-base mt-1">
                          {item.description}
                        </CardDescription>
                      </div>
                    </CardHeader>
                    {item.videoUrl && (
                      <CardContent>
                        <div className="aspect-video rounded-lg overflow-hidden">
                          <iframe
                            width="100%"
                            height="100%"
                            src={item.videoUrl}
                            title={item.artist}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            className="w-full h-full"
                          ></iframe>
                        </div>
                      </CardContent>
                    )}
                  </Card>
                ))}
              </div>
            </div>

            {/* Saturday */}
            <div>
              <h3 className="text-3xl font-bold mb-8 text-pink-400 text-center">SOBOTA 11. 7. 2026</h3>
              <div className="space-y-6">
                {programSchedule.saturday.map((item, index) => (
                  <Card key={index} className="bg-gray-800 border-pink-500 hover:shadow-lg hover:shadow-pink-500/50 transition-all">
                    <CardHeader>
                      <div>
                        <CardTitle className="text-2xl text-white">{item.artist}</CardTitle>
                        <CardDescription className="text-gray-400 text-base mt-1">
                          {item.description}
                        </CardDescription>
                      </div>
                    </CardHeader>
                    {item.videoUrl && (
                      <CardContent>
                        <div className="aspect-video rounded-lg overflow-hidden">
                          <iframe
                            width="100%"
                            height="100%"
                            src={item.videoUrl}
                            title={item.artist}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            className="w-full h-full"
                          ></iframe>
                        </div>
                      </CardContent>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form Section */}
      {showForm && (
        <section id="application-form" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-cyan-50 to-blue-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-5xl font-bold text-center mb-4">
              <span className="text-cyan-600">PŘIHLÁŠKA</span> <span className="text-gray-800">PRO PRODEJCE</span>
            </h2>
            <p className="text-center text-xl text-gray-600 mb-12">
              Vyplňte přihlášku a staňte se součástí Otrokovických letních slavností 2026
            </p>
            <OtrokoviceForm />
          </div>
        </section>
      )}

      {/* CTA Before Footer */}
      {!showForm && (
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-cyan-600 via-blue-500 to-cyan-600 text-white text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Připraveni se stát součástí festivalu?
            </h2>
            <p className="text-xl mb-10 text-cyan-100">
              Přihlaste se nyní a zajistěte si své místo na Otrokovických letních slavnostech 2026
            </p>
            <Button 
              size="lg" 
              onClick={scrollToForm}
              className="bg-pink-500 hover:bg-pink-600 text-white text-xl px-12 py-6 rounded-full font-bold shadow-2xl hover:shadow-pink-500/50 transition-all transform hover:scale-105"
            >
              PŘIHLÁSIT SE NYNÍ
            </Button>
          </div>
        </section>
      )}

      {/* More Events Section */}
      <section className="py-8 sm:py-16 px-4 sm:px-6 lg:px-8 bg-white border-y-2 sm:border-y-4 border-cyan-400">
        <div className="max-w-5xl mx-auto text-center">
          <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl sm:rounded-2xl p-6 sm:p-12 border-2 sm:border-4 border-cyan-300 shadow-xl">
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-black mb-3 sm:mb-4 text-cyan-700">
              🎪 Máme více festivalů!
            </h2>
            <p className="text-base sm:text-xl text-gray-700 mb-6 sm:mb-8 max-w-2xl mx-auto">
              Zajímá vás prodejní místo i na dalších akcích? Podívejte se na všechny naše festivaly v roce 2026.
            </p>
            <Button 
              onClick={() => navigate('/')}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white text-base sm:text-xl px-6 sm:px-12 py-4 sm:py-6 font-black rounded-full shadow-2xl hover:shadow-3xl transition-all w-full sm:w-auto"
            >
              🎉 Zobrazit další akce
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {/* Organizers */}
            <div>
              <h3 className="text-xl font-bold mb-4 text-cyan-400">Pořadatelé</h3>
              <div className="space-y-2">
                {contactInfo.organizers.map((org, index) => (
                  <p key={index} className="text-gray-300">{org.name}</p>
                ))}
              </div>
            </div>

            {/* Contact */}
            <div>
              <h3 className="text-xl font-bold mb-4 text-cyan-400">Kontakt</h3>
              <div className="space-y-2">
                <p className="text-gray-300">{contactInfo.contactPerson.name}</p>
                <p className="flex items-center gap-2 text-gray-300">
                  <Phone className="h-4 w-4" />
                  {contactInfo.contactPerson.phone}
                </p>
                <p className="flex items-center gap-2 text-gray-300">
                  <Mail className="h-4 w-4" />
                  {contactInfo.contactPerson.email}
                </p>
              </div>
            </div>

            {/* Website */}
            <div>
              <h3 className="text-xl font-bold mb-4 text-cyan-400">Web</h3>
              <p className="flex items-center gap-2 text-gray-300">
                <Globe className="h-4 w-4" />
                {contactInfo.website}
              </p>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-8 text-center">
            <p className="text-gray-400">© 2026 {contactInfo.website} - Všechna práva vyhrazena</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default OtrokoviceLanding;
